import { AuctionState, BusType, DirectSaleState, type SaleAssetBusEntity } from '~/apiClient';
import { isAuction, type PlatformSaleItemEntity } from '~/types/sale.type';

export type UseXdpSeoMetaReturnType = void;

export const useSdpSeoMeta = (sale: Ref<PlatformSaleItemEntity>): UseXdpSeoMetaReturnType => {
    // COMPOSABLES
    const { tt } = useTypedI18n();
    const { formatFirstRegistrationYear } = useAssetRegistration();
    const image = useImage();

    // DATA
    const mainAsset = computed<SaleAssetBusEntity>(() => sale.value.mainAsset);
    // The value carries its own separator: templates write `{type}{brand}`, so an untyped vehicle leaves no gap behind
    const type = computed<string>(() => {
        const busType = mainAsset.value.type;

        return busType && busType !== BusType.Other ? `${tt(`asset.busType.${busType}`)} ` : '';
    });
    const params = computed(() => ({
        type: type.value,
        brand: mainAsset.value.brand.brandValue,
        model: mainAsset.value.model.modelValue,
        year: formatFirstRegistrationYear(mainAsset.value),
    }));
    const title = computed<string>(() => tt(`seo.sdp.${sale.value.sellingMethod}.title`, params.value));
    const description = computed<string>(() => tt(`seo.sdp.${sale.value.sellingMethod}.description`, params.value));
    const isAvailable = computed<boolean>(() =>
        isAuction(sale.value) ? sale.value.state === AuctionState.Running : sale.value.state === DirectSaleState.Open
    );
    const cover = computed<string | undefined>(() => mainAsset.value.images.cover[0]);

    // SEO
    useSeoMeta({
        title: () => title.value,
        ogTitle: () => withSiteName(title.value),
        description: () => description.value,
        ogDescription: () => description.value,
        ...setIf(!!cover.value, () => ({
            ogImage: {
                url: image(cover.value!, { width: 1280, height: 960 }),
                width: '1280px',
                height: '960px',
                alt: `Fleequid ${mainAsset.value.brand.brandValue} ${mainAsset.value.model.modelValue} ${mainAsset.value.vin}`,
            },
        })),
    });

    // og:type product and the product:* namespace have no flat counterpart in useSeoMeta
    useHead(() => ({
        meta: [
            { property: 'og:type', content: 'product' },
            { property: 'product:availability', content: isAvailable.value ? 'in stock' : 'out of stock' },
            { property: 'product:condition', content: 'used' },
        ],
    }));
};
